import type { ComputedRef } from 'vue'
import type { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'
import type { CustomerKeyValueRow } from '@entities/customer'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'

const PLACEHOLDER = '-'
const ZIP_PLACEHOLDER = '---'

interface SectionCustomerStatusesReturn {
  hasDetails: ComputedRef<boolean>
  refOtpStatus: ComputedRef<string>
  refGeneral: ComputedRef<CustomerKeyValueRow[]>
  refStatus: ComputedRef<CustomerStatus | null>
  refSources: ComputedRef<CustomerKeyValueRow[]>
}

const yesNo = (value: boolean): string => (value ? 'Yes' : 'No')

/** The Statuses & sources tab — a read-only view of `customers.details`. */
export const useSectionCustomerStatuses = (): SectionCustomerStatusesReturn => {
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()

  const hasDetails = computed<boolean>(() => customerStore.hasDetails)

  const refOtpStatus = computed<string>(() => {
    const phoneStatus = customerStore.getDetails?.phone_status

    return phoneStatus ? (listsStore.getLists?.customer?.phone_status?.[phoneStatus] ?? phoneStatus) : ''
  })

  const refGeneral = computed<CustomerKeyValueRow[]>(() => {
    const details = customerStore.getDetails
    if (!details) return []

    return [
      { key: 'Screen recording:', value: yesNo(details.screen_recording) },
      { key: 'SQL:', value: details.sql || PLACEHOLDER },
      { key: 'Zip code:', value: details.zip_code || ZIP_PLACEHOLDER },
      { key: 'Is call reached:', value: yesNo(details.is_call_reached) },
      { key: 'WhatsApp opt-out:', value: yesNo(details.wa_opt_out) },
      { key: 'Pain:', value: details.pain || PLACEHOLDER },
    ]
  })

  const refStatus = computed<CustomerStatus | null>(() =>
    (customerStore.getDetails?.status ?? null) as CustomerStatus | null)

  const refSources = computed<CustomerKeyValueRow[]>(() => {
    const details = customerStore.getDetails
    if (!details) return []

    return [
      { key: 'Source funnel:', value: details.source_funnel ?? '' },
      { key: 'Campaigns:', value: details.campaigns.join() || PLACEHOLDER },
      { key: 'Tags:', value: details.tags.join() || PLACEHOLDER },
    ]
  })

  return {
    hasDetails,
    refOtpStatus,
    refGeneral,
    refStatus,
    refSources,
  }
}
